import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import {
  Shield,
  Users,
  Database,
  GitMerge,
  Search,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  Fingerprint,
  Network,
  Lock,
  BarChart3,
  Brain,
  Sparkles,
} from 'lucide-react';

// Animated counter hook
function useCounter(end: number, duration: number = 2000, startOnView: boolean = true) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!startOnView || isInView) {
      let startTime: number;
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [end, duration, isInView, startOnView]);

  return { count, ref };
}

// Floating particles background
function ParticleField() {
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 4 + 1,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-primary-500/20"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

// Animated grid background
function GridBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f2937_1px,transparent_1px),linear-gradient(to_bottom,#1f2937_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
    </div>
  );
}

// Glowing orb component
function GlowingOrb({ className = '', delay = 0 }: { className?: string; delay?: number }) {
  return (
    <motion.div
      className={`absolute rounded-full blur-3xl ${className}`}
      animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3],
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        delay,
        ease: 'easeInOut',
      }}
    />
  );
}

// Feature card component
function FeatureCard({
  icon: Icon,
  title,
  description,
  index,
}: {
  icon: typeof Shield;
  title: string;
  description: string;
  index: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 h-full hover:border-primary-500/50 transition-all duration-300">
        <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-cyan-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
          <Icon className="w-7 h-7 text-white" />
        </div>
        <h3 className="text-xl font-bold text-white mb-3">{title}</h3>
        <p className="text-gray-400 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
}

// Process step component
function ProcessStep({
  number,
  title,
  description,
  icon: Icon,
  index,
}: {
  number: string;
  title: string;
  description: string;
  icon: typeof Database;
  index: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.2 }}
      className="relative flex items-start gap-6"
    >
      <div className="flex-shrink-0">
        <motion.div
          whileHover={{ rotate: 360 }}
          transition={{ duration: 0.6 }}
          className="w-16 h-16 bg-gradient-to-br from-primary-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/25"
        >
          <Icon className="w-8 h-8 text-white" />
        </motion.div>
      </div>
      <div className="flex-1 pt-2">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-sm font-mono text-primary-400">{number}</span>
          <div className="h-px flex-1 bg-gradient-to-r from-primary-500/50 to-transparent" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
        <p className="text-gray-400">{description}</p>
      </div>
    </motion.div>
  );
}

// Stat card component
function StatCard({
  value,
  label,
  suffix = '',
  index,
}: {
  value: number;
  label: string;
  suffix?: string;
  index: number;
}) {
  const { count, ref } = useCounter(value, 2000);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.5 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="text-center"
    >
      <motion.div
        className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary-400 via-cyan-400 to-primary-400 bg-clip-text text-transparent"
        animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
        transition={{ duration: 5, repeat: Infinity }}
        style={{ backgroundSize: '200%' }}
      >
        {count.toLocaleString()}{suffix}
      </motion.div>
      <div className="text-gray-400 mt-2 text-lg">{label}</div>
    </motion.div>
  );
}

// Main landing page
export default function LandingPage() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ container: scrollContainerRef });
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const features = [
    {
      icon: Fingerprint,
      title: 'Entity Resolution',
      description: 'Advanced AI-powered matching algorithms identify duplicate records and resolve entities across multiple data sources with unprecedented accuracy.',
    },
    {
      icon: Shield,
      title: 'Regulatory Compliance',
      description: 'Stay compliant with Nevada Gaming Commission requirements. Automatic DAP list screening and real-time exclusion monitoring.',
    },
    {
      icon: Network,
      title: 'Relationship Discovery',
      description: 'Uncover hidden connections between entities. Identify family members, business associates, and potential fraud networks.',
    },
    {
      icon: Brain,
      title: 'Intelligent Matching',
      description: 'Machine learning models continuously improve matching accuracy. Understand why records match with detailed confidence scoring.',
    },
    {
      icon: GitMerge,
      title: 'Adjudication Workflows',
      description: 'Streamlined review process for possible matches. Side-by-side comparison with one-click merge or reject actions.',
    },
    {
      icon: Lock,
      title: 'Audit Trail',
      description: 'Complete transparency with full audit logging. Track every decision, merge, and data change for regulatory reporting.',
    },
  ];

  const processSteps = [
    {
      number: '01',
      icon: Database,
      title: 'Ingest Data Sources',
      description: 'Upload patron databases, loyalty programs, and exclusion lists. Support for CSV, Excel, and direct database connections.',
    },
    {
      number: '02',
      icon: Search,
      title: 'Entity Resolution',
      description: 'Our engine analyzes records, identifies matches, and creates unified entity profiles with confidence scores.',
    },
    {
      number: '03',
      icon: AlertTriangle,
      title: 'Conflict Detection',
      description: 'Automatic screening against exclusion lists. DAP alerts surface immediately for compliance review.',
    },
    {
      number: '04',
      icon: CheckCircle,
      title: 'Review & Action',
      description: 'Adjudicate matches, confirm identities, and take action. Full audit trail for every decision.',
    },
  ];

  // Hide body scroll when landing page is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <div ref={scrollContainerRef} className="h-screen bg-gray-900 text-white overflow-y-auto overflow-x-hidden">
      {/* Progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-cyan-500 to-primary-500 z-50 origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Hero Section */}
      <motion.section
        style={{ opacity: heroOpacity, scale: heroScale }}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        <GridBackground />
        <ParticleField />
        <GlowingOrb className="w-96 h-96 bg-primary-500 -top-48 -left-48" />
        <GlowingOrb className="w-96 h-96 bg-cyan-500 -bottom-48 -right-48" delay={2} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          <AnimatePresence>
            {isLoaded && (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="inline-flex items-center gap-2 bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-full px-4 py-2 mb-8"
                >
                  <Sparkles className="w-4 h-4 text-primary-400" />
                  <span className="text-sm text-gray-300">Powered by Advanced Entity Resolution</span>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="mb-8"
                >
                  <img
                    src="/Syntrove_Core_Logo_white.png"
                    alt="Syntrove Core"
                    className="h-24 md:h-32 lg:h-40 mx-auto object-contain"
                  />
                </motion.div>

                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="text-xl md:text-2xl text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed"
                >
                  Enterprise-grade entity resolution platform for gaming compliance.
                  Identify duplicates, screen exclusions, and protect your operations.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                  <Link to="/login">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="group relative px-8 py-4 bg-gradient-to-r from-primary-500 to-cyan-500 rounded-xl font-semibold text-lg shadow-lg shadow-primary-500/25 overflow-hidden"
                    >
                      <span className="relative z-10 flex items-center gap-2">
                        Enter Platform
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-primary-500"
                        initial={{ x: '100%' }}
                        whileHover={{ x: 0 }}
                        transition={{ duration: 0.3 }}
                      />
                    </motion.button>
                  </Link>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-8 py-4 border border-gray-600 rounded-xl font-semibold text-lg hover:border-gray-500 hover:bg-gray-800/50 transition-all"
                  >
                    Watch Demo
                  </motion.button>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="absolute bottom-8 left-1/2 -translate-x-1/2"
                >
                  <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <ChevronDown className="w-8 h-8 text-gray-500" />
                  </motion.div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </motion.section>

      {/* Stats Section */}
      <section className="relative py-24 border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <StatCard value={99835} label="Entities Resolved" index={0} />
            <StatCard value={448} label="Possible Matches" index={1} />
            <StatCard value={99} label="Match Accuracy" suffix="%" index={2} />
            <StatCard value={24} label="Hour Monitoring" suffix="/7" index={3} />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-32">
        <GlowingOrb className="w-[600px] h-[600px] bg-primary-500/30 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                Powerful Features
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything you need to manage entity resolution at scale
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard key={feature.title} {...feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative py-32 bg-gray-800/30">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                How It Works
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              From data ingestion to actionable insights in four steps
            </p>
          </motion.div>

          <div className="space-y-12">
            {processSteps.map((step, index) => (
              <ProcessStep key={step.number} {...step} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/20 via-transparent to-cyan-900/20" />

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-full px-4 py-2 mb-6">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400">Regulatory Compliance</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
                  DAP Screening &
                </span>
                <br />
                <span className="bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                  Exclusion Monitoring
                </span>
              </h2>
              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                Automatically screen patrons against Nevada Gaming Commission exclusion lists.
                Real-time alerts when a match is detected, with full audit trail for compliance reporting.
              </p>
              <ul className="space-y-4">
                {[
                  'Real-time DAP list screening',
                  'Automatic conflict detection',
                  'Severity-based prioritization',
                  'Complete audit logging',
                ].map((item, index) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center gap-3 text-gray-300"
                  >
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-3xl blur-3xl" />
              <div className="relative bg-gray-800/80 backdrop-blur-sm border border-gray-700 rounded-3xl p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      <div>
                        <div className="font-medium text-white">DAP Alert</div>
                        <div className="text-sm text-gray-400">High confidence match detected</div>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm font-medium">
                      Critical
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="font-medium text-white">JOHN DOE</div>
                        <div className="text-sm text-gray-400">Entity #142857</div>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-medium">
                      Review
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="w-5 h-5 text-gray-400" />
                      <div>
                        <div className="font-medium text-white">Match Score</div>
                        <div className="text-sm text-gray-400">+NAME+DOB+ADDRESS</div>
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-white">94%</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary-900/10 to-transparent" />
        <GlowingOrb className="w-[800px] h-[800px] bg-primary-500/20 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
                Ready to Transform
              </span>
              <br />
              <span className="bg-gradient-to-r from-primary-400 to-cyan-400 bg-clip-text text-transparent">
                Your Data Operations?
              </span>
            </h2>
            <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
              Join leading gaming operators who trust Syntrove Core for entity resolution and compliance.
            </p>
            <Link to="/login">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-12 py-5 bg-gradient-to-r from-primary-500 to-cyan-500 rounded-xl font-semibold text-xl shadow-2xl shadow-primary-500/25 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-3">
                  Get Started Now
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-primary-500"
                  initial={{ x: '100%' }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img
                src="/Syntrove_Core_Logo_white.png"
                alt="Syntrove Core"
                className="h-10 object-contain"
              />
            </div>
            <div className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Syntrove. Enterprise Entity Resolution Platform.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
